import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useSession } from '../context/SessionContext';
import { set } from 'date-fns';

export default function useDashboardController() {
  const { session } = useSession();
  const [cvs, setCvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cvLoading, setCvLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [username, setUsername] = useState('');
  const [title, setTitle] = useState('');
  const [bio, setBio] = useState('');
  const [selectedCvId, setSelectedCvId] = useState(null);

  const fetchUsername = async () => {
    try {
      setProfileLoading(true);
      if (!session?.user?.id) {
        setMessage('fetching username');
        throw new Error('User session not available');
      }
      const { data, error } = await supabase
        .from('profile')
        .select('username, title_web, bio_web, selected_cv_id')
        .eq('user_id', session.user.id)
        .limit(1);

      if (error) throw error;
      if (data.length > 0) {
        setUsername(data[0].username || '');
        setTitle(data[0].title_web || '');
        setBio(data[0].bio_web || '');
        setSelectedCvId(data[0].selected_cv_id || null);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to fetch profile data');
    } finally {
      setProfileLoading(false);
    }
  };

  const checkUsername = async (newUsername) => {
    try {
      if (!session?.user?.id) {
        setMessage('checking username');
        throw new Error('User session not available');
      }
      const { data, error } = await supabase
        .from('profile')
        .select('username')
        .eq('username', newUsername)
        .neq('user_id', session.user.id);

      if (error) throw error;
      return data.length > 0;
    } catch (error) {
      console.error('Error checking username:', error);
      throw error;
    }
  };

  const updateProfileWithUsername = async (newUsername, newTitle, newBio, selectedCvId) => {
    try {
      const usernameRegex = /^[a-zA-Z0-9_]+$/;
      if (!usernameRegex.test(newUsername)) {
        throw new Error('Username can only contain letters, numbers, and underscores');
      }

      const usernameExists = await checkUsername(newUsername);
      if (usernameExists) {
        throw new Error('Username is already taken');
      }

      if (!newTitle.trim()) {
        throw new Error('Title cannot be empty');
      }

      if (!newBio.trim()) {
        throw new Error('Bio cannot be empty');
      }

      const { data: existingProfile } = await supabase
        .from('profile')
        .select('user_id')
        .eq('user_id', session.user.id)
        .limit(1);

      const { error } = await supabase
        .from('profile')
        [existingProfile.length > 0 ? 'update' : 'insert']({
          user_id: session.user.id,
          username: newUsername,
          title_web: newTitle,
          bio_web: newBio,
          selected_cv_id: selectedCvId || null
        })
        .eq('user_id', session.user.id);

      if (error) throw error;

      setUsername(newUsername);
      setTitle(newTitle);
      setBio(newBio);
      setSelectedCvId(selectedCvId);
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.message);
      return false;
    }
  };

  const createWebPage = async (newUsername, newTitle, newBio, selectedCvId) => {
    try {
      setProfileLoading(true);
      setError(null);

      if (!newUsername.trim()) {
        throw new Error('Username cannot be empty');
      }

      const success = await updateProfileWithUsername(newUsername, newTitle, newBio, selectedCvId);
      return success;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchCVs = async () => {
    try {
      setCvLoading(true);
      if (!session?.user?.id) {
        setMessage('fetching CVs');
        throw new Error('User session not available');
      }
      const { data, error } = await supabase
        .from('cv')
        .select('*')
        .eq('user_id', session.user.id);
  
      if (error) throw error;
  
      if (!data || data.length === 0) {
        setMessage('No CV Data Found, You should create a new one');
      } else {
        setMessage('');
      }
  
      setCvs(data || []);
    } catch (error) {
      console.error('Error fetching CVs:', error);
      setError('Failed to fetch CV data');
    } finally {
      setCvLoading(false);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    const fetchData = async () => {
      if (session?.user?.id) {
        await fetchUsername();
        await fetchCVs();
      }
    };
  
    fetchData();
  
    return () => {
      abortController.abort();
      setCvs([]);
      setUsername('');
      setTitle('');
      setBio('');
      setSelectedCvId(null);
    };
  }, [session]); 

  return {
    username,
    title,
    bio,
    setUsername,
    cvs,
    loading: cvLoading || profileLoading,
    error,
    message,
    createWebPage,
    fetchCVs,
    selectedCvId,
    setSelectedCvId
  };
}